const PORTAL_ID = "5901754"

/**
 * Gets a cookie value by name
 * @param {string} name - Cookie name to retrieve
 * @returns {string|null} - Cookie value or null if not found
 */
function getCookie(name) {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? match[2] : null
}

/**
 * Gets the current user's HubSpot tracking cookie (hutk)
 * @note If the user has disabled tracking or cookies, this value may not be available.
 * @returns {string|null} - The hubspotutk cookie value or null
 */
function getHubspotUserToken() {
  return getCookie("hubspotutk")
}

/**
 * Fetches the visitor's IP address using a public API
 * @note This is optional - form submissions will work without IP, but analytics may be affected
 * @returns {Promise<string|null>} - IP address or null if unavailable
 */
async function getVisitorIpAddress() {
  try {
    const response = await fetch("https://api.ipify.org?format=json", {
      signal: AbortSignal.timeout(3000), // 3 second timeout
    })
    if (response.ok) {
      const data = await response.json()
      return data.ip
    }
  } catch {
    // IP lookup failed - continue without it
  }
  return null
}

/**
 * Submits a HubSpot form via HubSpot's Forms API
 * @see https://developers.hubspot.com/docs/methods/forms/submit_form_v3
 * @param {string} formId Hubspot form ID
 * @param {Array[Object<string,*>]} data Form data
 * @returns {Promise}
 */
async function submitHubspotForm(formId, data) {
  // Fetch IP address in parallel with building the request (best-effort, non-blocking)
  const ipAddress = await getVisitorIpAddress()

  const hutk = getHubspotUserToken()
  const context = {
    pageUri: typeof window !== "undefined" ? window.location.href : undefined,
    pageName: typeof document !== "undefined" ? document.title : undefined,
  }

  // Only include hutk/ipAddress if we have a valid value
  if (hutk) context.hutk = hutk
  if (ipAddress) context.ipAddress = ipAddress

  const fields = Object.keys(data).map((name) => ({
    name,
    value: data[name],
  }))

  // Bridge Segment identity into the CRM
  const anonymousId =
    typeof window !== "undefined"
      ? window.analytics?.user?.()?.anonymousId?.()
      : null
  if (anonymousId) {
    fields.push({ name: "segment_anonymous_id", value: anonymousId })
  }

  const body = { context, fields }

  return fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${formId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  )
}

export default submitHubspotForm
